import { useState } from 'react';
import { Alert, Button, Input, Select, Space, Tabs, Upload } from 'antd';
import { GoogleOutlined, InboxOutlined } from '@ant-design/icons';
import { ApiError } from '../api/client.ts';
import { fetchRemoteImportFile } from './api.ts';
import { readWorkbook } from './workbook.ts';

const { Dragger } = Upload;

/**
 * Источник файла для импорт-модалок: «С компьютера» (drag-n-drop)
 * или «Google Drive / Sheets» (вставить share-ссылку → серверный
 * прокси скачает файл).
 *
 * Контракт `onLoaded(file, sheetName)` одинаковый для обоих табов:
 * вызывающая модалка не знает, откуда пришёл `File`. `sheetName` —
 * выбранный пользователем лист (для книг с несколькими вкладками);
 * если лист один, он передаётся автоматически без лишнего шага.
 */
interface Props {
  /** `accept` для `<Upload>`, например `.csv,.xlsx,.xls`. */
  accept: string;
  /** Подпись с подсказкой о форматах под Dragger. */
  formatsHint?: string;
  onLoaded: (file: File, sheetName: string) => void;
}

export function RemoteSourcePanel({ accept, formatsHint, onLoaded }: Props): JSX.Element {
  const [url, setUrl] = useState('');
  // Вкладка источника хранится в состоянии, а не в `defaultActiveKey`:
  // шаг выбора листа рендерится вместо `<Tabs>`, и при возврате
  // неконтролируемый Tabs сбрасывался на «С компьютера» — файл был
  // взят из Google Drive, а открытой оказывалась чужая вкладка.
  const [activeTab, setActiveTab] = useState('file');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Книга с >1 листом: ждём, пока пользователь выберет вкладку, вместо
  // молчаливого `SheetNames[0]`.
  const [pending, setPending] = useState<{ file: File; sheetNames: string[] } | null>(null);
  const [selectedSheet, setSelectedSheet] = useState('');

  /**
   * Общий вход для обоих табов: читаем книгу и решаем, нужен ли шаг выбора
   * листа. Один лист (в т.ч. любой CSV) — сразу отдаём наверх; несколько —
   * показываем селектор вкладок.
   */
  const ingestFile = async (file: File): Promise<void> => {
    setErr(null);
    try {
      const wb = await readWorkbook(file);
      const names = wb.SheetNames;
      if (names.length === 0) {
        setErr('Файл не содержит листов');
        return;
      }
      if (names.length === 1) {
        onLoaded(file, names[0]!);
        return;
      }
      setPending({ file, sheetNames: names });
      setSelectedSheet(names[0]!);
    } catch (e) {
      setErr(e instanceof Error ? `Не удалось прочитать файл: ${e.message}` : 'Не удалось прочитать файл');
    }
  };

  const handleFile = (file: File): boolean => {
    void ingestFile(file);
    return false; // не отправляем файл через antd Upload
  };

  const handleFetch = async (): Promise<void> => {
    if (!url.trim()) return;
    setLoading(true);
    setErr(null);
    try {
      const file = await fetchRemoteImportFile(url.trim());
      await ingestFile(file);
    } catch (e) {
      if (e instanceof ApiError) setErr(e.message);
      else setErr(e instanceof Error ? e.message : 'Не удалось получить файл');
    } finally {
      setLoading(false);
    }
  };

  // Шаг выбора листа — показывается только для книг с несколькими вкладками.
  if (pending) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Alert
          type="info"
          showIcon
          message="В файле несколько листов"
          description="Выберите вкладку, из которой импортировать данные."
        />
        <Select
          style={{ width: '100%' }}
          value={selectedSheet}
          onChange={setSelectedSheet}
          options={pending.sheetNames.map((name) => ({ value: name, label: name }))}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button
            onClick={() => {
              setPending(null);
              setSelectedSheet('');
            }}
          >
            Назад
          </Button>
          <Button
            type="primary"
            disabled={!selectedSheet}
            onClick={() => {
              onLoaded(pending.file, selectedSheet);
              setPending(null);
              setSelectedSheet('');
            }}
          >
            Выбрать лист
          </Button>
        </div>
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'file',
            label: 'С компьютера',
            children: (
              <Dragger
                accept={accept}
                multiple={false}
                beforeUpload={handleFile}
                showUploadList={false}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Нажмите или перетащите файл</p>
                <p className="ant-upload-hint">{formatsHint ?? accept}</p>
              </Dragger>
            ),
          },
          {
            key: 'gdrive',
            label: (
              <span>
                <GoogleOutlined /> Google Drive / Sheets
              </span>
            ),
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <Alert
                  type="info"
                  showIcon
                  message="Откройте доступ «Все, у кого есть ссылка»"
                  description="Скопируйте share-ссылку из Google Sheets или Google Drive и вставьте сюда. Лист скачивается как XLSX автоматически."
                />
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onPressEnter={() => void handleFetch()}
                    disabled={loading}
                  />
                  <Button
                    type="primary"
                    loading={loading}
                    disabled={!url.trim()}
                    onClick={() => void handleFetch()}
                  >
                    Загрузить
                  </Button>
                </Space.Compact>
              </Space>
            ),
          },
        ]}
      />
      {err && <Alert type="error" showIcon message={err} />}
    </Space>
  );
}
